import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { z as zod, ZodObject, ZodTypeAny } from 'zod';
import { PulseLoader } from 'react-spinners';
import { isValidPhoneNumber } from 'react-phone-number-input';
import i18next from 'i18next';
import { useLocation } from 'react-router';
import { decodeJWT } from 'aws-amplify/auth';
import { schemaHelper } from 'src/components/hook-form';
import { useTranslate } from 'src/locales';
import FormConstants from 'src/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import {
  executeAdminActions,
  getAdminDefaultValues,
  getDndDraggableList,
  getUserDefaultValues,
} from 'src/services/service-handlers/PrivateService';
import AuthUtils from 'src/utils/AuthUtils';
import { toast } from 'src/components/snackbar';
import { FormBuilder } from './form-builder';
import { useAuthContext } from 'src/context/AuthContext';
import { injectTemplateFields } from 'src/utils/templateUtils';
import { truncate } from 'node:fs';
import { LoadingButton } from '@mui/lab';
import { useHasPermission } from 'src/utils/helper';

interface Props {
  formInitUrl?: string;
  fetchDefaultValues?: boolean;
  onSubmit?: (data: any) => void;
  stageMessage?: string;
  isAdmin?: boolean;
  queryDependencyIds?: Record<string, any>;
  formDataParsed?: FormData;
  tabValue?: string;
  module_name?: string;
  sub_module?: string;
  isFullApplicationEnabled?: boolean;
  fullApplicationIncomeCategory?: number;
  person_id?: any;
  scrollNeeded?: boolean;
  signupType?: any;
  onRefreshFullApplicationStatus?: () => void;
}

interface RadioOptions {
  label: string;
  value: any;
  countryCode: string;
}

interface Column {
  name: string;
  label: string;
  type: string;
  options?: RadioOptions[];
  heading?: string;
  validation_schema?: string;
  required?: boolean;
  columns?: any;
}

interface Condition {
  fieldName: string;
  fieldValue: any;
}

interface FieldData {
  conditionalRender?: boolean;
  condition?: Condition;
  label: string;
  name: string;
  required?: boolean;
  sub_heading?: string;
  heading?: string;
  placeholder?: string;
  helper_text?: string;
  options?: RadioOptions[];
  depends_on_field?: string;
  filter_options_with_col?: string;
  columns?: Column[];
  type: string;
  defaultValue?: any;
  validation_schema?: keyof typeof validationMapping;
  is_heading_only_block?: boolean;
  signupType?: any;
}

interface FileParameters {
  accept_file_size_bytes: number;
  accept_file_types: string[];
  required_level: string;
  prevent_submit_if_empty: boolean;
}

interface UploadsRowData {
  label: string;
  description_text: string;
  file_parameters: FileParameters;
}

interface SectionData {
  order: number;
  section_label: string;
  hash?: string;
  render_vertical?: boolean;
  is_upload_section?: boolean;
  fields: FieldData[];
  rows: UploadsRowData[];
}

export interface FormData {
  form_type: string;
  sections: SectionData[];
  translations: Record<string, Record<string, string>>;

  // From form-builder.tsx
  showAuditInfo?: boolean;
  show_cancel_button?: boolean;
  submit_button_text: any; // kept 'any' since builder uses any
  rescind_button_text?: string;
  emailTemplate?: any;
  emailTemplates?: any;
  submit_button_sql?: any;
  notification_sql_data?: any;
  is_notification_enabled?: boolean;
  submit_button_insert_sql: any;
  submit_button_update_sql: any;
  rescind_button_sql?: any;
  requires_confirmation?: boolean;
  confirmation?: any;
  terms_acknowledgement?: boolean;
  terms_acknowledgement_text?: string;
  terms_acknowledgement_body?: string;
  disabled_by_default?: boolean;
  encrypted_key_on_submit?: string;
  get_raw_data_sql?: string;

  // Flags
  conditional_disable_form?: boolean;
  disable_condition?: {
    // Support both notations
    fieldName?: string;
    fieldValue?: any;
    conditionField?: string;
    conditionValue?: any;
  };
  disable_isDirty?: boolean;
  edit_button_text?: string;
  edit_button_sql?: any;
  helper_message?: any;

  // From form-loader.tsx
  sql_data?: any;
  form_actions?: any;
  enable_otp_verification?: boolean;
}

interface UserPayload {
  PersonId: string;
  UserId: string;
  RecordId: string;
}

const validationMapping = {
  name: schemaHelper.name,
  email: schemaHelper.email,
  phoneNumber: schemaHelper.phoneNumber,
  referral: schemaHelper.referral,
  text: schemaHelper.text,
  password: schemaHelper.password,
  date: schemaHelper.date,
  editor: schemaHelper.editor,
  objectOrNull: schemaHelper.objectOrNull,
  boolean: schemaHelper.boolean,
  file: schemaHelper.file,
  files: schemaHelper.files,
  number: schemaHelper.number,
  floatNumber: schemaHelper.floatNumber,
  array: schemaHelper.array,
};

const coerceIfNumeric = (val: any) => {
  if (val === '' || val === null || val === undefined) return val;
  const n = Number(val);
  return Number.isNaN(n) ? val : n;
};

/** Recursively build a schema for a table_input column list */
function buildTableSchema(columns: Column[], t: (key: string) => string) {
  return zod.object(
    columns.reduce<Record<string, ZodTypeAny>>((shape, col) => {
      // ── ❶ Nested table_input ─────────────────────────────────────────────
      if (col.type === FormConstants.Field_Types.TABLE_INPUT && col.columns) {
        const nested = buildTableSchema(col.columns, t);
        shape[col.name] = col.required
          ? zod
              .array(nested)
              .min(1, `${t(col.label ?? col.heading ?? 'Field')} must contain at least one row!`)
          : zod.array(nested).optional();
        return shape;
      }

      // ── ❷ Scalar field ───────────────────────────────────────────────────
      const vfn = col.validation_schema
        ? validationMapping[col.validation_schema as keyof typeof validationMapping]
        : undefined;
      const labelOrHeading = col.label?.trim() || col.heading?.trim() || 'Field';
      const base =
        typeof vfn === 'function'
          ? vfn({
              message: {
                required_error: `${t(labelOrHeading)} is required!`,
                invalid_type_error: `${t(labelOrHeading)} is invalid!`,
              },
              required: !!col.required,
              isValidPhoneNumber,
            })
          : (vfn ?? zod.string());

      shape[col.name] = col.required
        ? base
        : zod.preprocess((v) => (v === '' || v === undefined ? undefined : v), base.optional());
      return shape;
    }, {})
  );
}

/** Build schema for a top-level field */
function buildFieldSchema(field: FieldData, t: (key: string) => string): ZodTypeAny {
  const { required, type, validation_schema } = field;

  // Table input at root level
  if (type === FormConstants.Field_Types.TABLE_INPUT && field.columns) {
    const tbl = buildTableSchema(field.columns, t);
    return required
      ? zod
          .array(tbl)
          .min(1, `${t(field.label ?? field.heading ?? 'Field')} must contain at least one row!`)
      : zod.array(tbl).optional();
  }

  // Scalar
  const vfn = validation_schema && validationMapping[validation_schema];
  const labelOrHeading = field.label?.trim() || field.heading?.trim() || 'Field';
  const base =
    typeof vfn === 'function'
      ? vfn({
          message: {
            required_error: `${t(labelOrHeading)} is required!`,
            invalid_type_error: `${t(labelOrHeading)} is invalid!`,
          },
          required: !!required,
          isValidPhoneNumber,
        })
      : (vfn ?? zod.string());

  return required
    ? base
    : zod.preprocess((v) => (v === '' || v === undefined ? undefined : v), base.optional());
}

function createFormSchema(formData: FormData, t: (key: string) => string) {
  const shape: Record<string, ZodTypeAny> = {};
  formData.sections.forEach((section) => {
    section.fields?.forEach((field) => {
      shape[field.name] = buildFieldSchema(field, t);
    });
  });
  return zod.object(shape);
}

// function createFormSchema(
//   formData: FormData,
//   t: (key: string) => string
// ): ZodObject<Record<string, ZodTypeAny>> {
//   const schemaShape: Record<string, ZodTypeAny> = {};

//   formData.sections.forEach((section) => {
//     if (!section.fields) return; // Skip section if it has no fields
//     section.fields.forEach((field) => {
//       const { name, required, columns, validation_schema, type } = field;
//       const validationFunction = validation_schema ? validationMapping[validation_schema] : null;

//       if (!validationFunction) {
//         if (type === FormConstants.Field_Types.TABLE_INPUT && field.columns) {
//           // Create schema for table input by processing each column
//           const tableSchema = zod.object(
//             field.columns.reduce<Record<string, ZodTypeAny>>((colShape, col) => {
//               const colValidationFn = col.validation_schema
//                 ? validationMapping[col.validation_schema as keyof typeof validationMapping]
//                 : zod.string(); // Default to string if no validation_schema is provided

//               const labelOrHeading = col.label?.trim() || col.heading?.trim() || 'Field';

//               const validationSchema =
//                 typeof colValidationFn === 'function'
//                   ? colValidationFn({
//                       message: {
//                         required_error: `${t(labelOrHeading)} is required!`,
//                         invalid_type_error: `${t(labelOrHeading)} is invalid!`,
//                       },
//                     })
//                   : colValidationFn;

//               colShape[col.name] = col.required
//                 ? validationSchema
//                 : zod.preprocess(
//                     (value) => (value === '' || value === undefined ? undefined : value),
//                     validationSchema.optional()
//                   );

//               return colShape;
//             }, {})
//           );

//           schemaShape[name] = required
//             ? zod.array(tableSchema).min(1, `${t(field.label)} must contain at least one row!`)
//             : zod.array(tableSchema).optional();
//         } else {
//           schemaShape[name] = required
//             ? zod.string().min(1, `${t(field.label)} is required!`)
//             : zod.string().optional();
//         }
//       } else {
//         const labelOrHeading = field.label?.trim() || field.heading?.trim() || 'Field';

//         const options = {
//           message: {
//             required_error: `${t(labelOrHeading)} is required!`,
//             invalid_type_error: `${t(labelOrHeading)} is invalid!`,
//           },
//           required: !!required,
//           isValidPhoneNumber,
//         };

//         schemaShape[name] = required
//           ? validationFunction(options)
//           : zod.preprocess(
//               (value) => (value === '' || value === undefined ? undefined : value),
//               validationFunction(options).optional()
//             );
//       }
//     });
//   });

//   return zod.object(schemaShape);
// }

export function FormLoader({
  formInitUrl,
  onSubmit,
  queryDependencyIds: ids,
  fetchDefaultValues = false,
  stageMessage,
  isAdmin = false,
  formDataParsed,
  tabValue,
  module_name,
  sub_module,
  isFullApplicationEnabled,
  fullApplicationIncomeCategory,
  person_id,
  scrollNeeded = true,
  signupType,
  onRefreshFullApplicationStatus,
}: Props) {
  const location = useLocation();
  const { authState } = useAuthContext();
  const { isAuthenticated } = authState;
  const [defaultValues, setDefaultValues] = useState<Record<string, string> | null>(null);
  const [schema, setSchema] = useState<ZodObject<Record<string, ZodTypeAny>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [StageMessage, setStageMessage] = useState<string | undefined>(undefined);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedActionValue, setSelectedActionValue] = useState('');
  const [pendingAction, setPendingAction] = useState<any>(null);
  const [fromStage, setFromStage] = useState<string>('');
  const [toStage, setToStage] = useState<string>('');
  const [reason, setReason] = useState<any>(null);
  const [reasonError, setReasonError] = useState('');
  const [defaultData, setDefaultData] = useState<any>(null);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [properties, setProperties] = useState<any[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<any>(null);
  const [dialogOnSubmitLoading, setDialogOnSubmitLoading] = useState(false);

  // ───────── permissions management ──────────────────────────────────────────────
  const hasPermission = useHasPermission();

  const token = isAuthenticated ? AuthUtils.getAuthToken() : null;
  const decodedToken = isAuthenticated && token ? decodeJWT(token) : null;
  const tokenData = decodedToken as { payload?: { user?: UserPayload } } | null;

  const queryDependencyIds: Record<string, any> = EncryptUtils.getDecryptedData(
    location.search,
    'row'
  );

  const personID = tokenData?.payload?.user?.PersonId;

  const { t } = useTranslate('form');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryDependencyIdsString = JSON.stringify(queryDependencyIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableQueryDependencyIds = useMemo(() => queryDependencyIds, [queryDependencyIdsString]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFormDataParsed = useMemo(() => formDataParsed, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchFormData = useCallback(async () => {
    try {
      const formData = stableFormDataParsed;
      // console.log(formData, 'formData from formLoader');

      if (!formData || !module_name) {
        return;
      }

      const { sql_data, translations } = formData;

      if (translations) {
        Object.keys(translations)?.forEach((lang) => {
          i18next.addResourceBundle(lang, 'form', translations[lang]);
        });
      }

      let resp: Record<string, any> = {};
      // console.log(stableQueryDependencyIds, 'stableQueryDependencyIds');

      if (fetchDefaultValues) {
        if (isAdmin && (stableQueryDependencyIds || person_id)) {
          const data = {
            sql_data: sql_data,
            person_id: stableQueryDependencyIds.PersonId || person_id,
            ...stableQueryDependencyIds,
          };
          const encryptRequestData = EncryptUtils.encryptedData(data);
          resp = await getAdminDefaultValues(module_name, { tk: encryptRequestData });
        } else {
          const data = {
            sql_data: sql_data,
            person_id: personID,
            ...(stableQueryDependencyIds && stableQueryDependencyIds),
          };
          const encryptRequestData = EncryptUtils.encryptedData(data);
          resp = await getUserDefaultValues(module_name, { tk: encryptRequestData });
        }
      }

      const defaultValueSource = resp?.data?.[0] || {};
      setDefaultData(defaultValueSource);

      if (defaultValueSource.StageMessage) {
        setStageMessage(defaultValueSource.StageMessage);
      }

      const tableInputFields =
        formData?.sections?.flatMap((section) =>
          section?.fields
            ?.filter((field) => field.type === 'table_input')
            .map((field) => field.name)
        ) || [];

      tableInputFields?.forEach((fieldName) => {
        if (defaultValueSource?.[fieldName] && typeof defaultValueSource[fieldName] === 'string') {
          try {
            defaultValueSource[fieldName] = JSON.parse(defaultValueSource[fieldName]);
          } catch (err) {
            console.error(`Error parsing table input field "${fieldName}":`, err);
            defaultValueSource[fieldName] = [];
          }
        }
      });

      /* ───────── NEW RECURSIVE SECTION ───────── */
      /** Walk every (nested) table-input column and parse JSON blobs that came back as strings */
      const parseNestedTables = (obj: any, columns: Column[] = []) => {
        columns.forEach((col) => {
          if (!obj) return;
          const val = obj[col.name];

          if (col.type === FormConstants.Field_Types.TABLE_INPUT) {
            if (val && typeof val === 'string') {
              try {
                obj[col.name] = JSON.parse(val);
              } catch {
                obj[col.name] = [];
              }
            }
            if (Array.isArray(obj[col.name])) {
              obj[col.name].forEach((row: any) => parseNestedTables(row, col.columns ?? []));
            }
          } else if (
            col.validation_schema === 'number' ||
            col.validation_schema === 'floatNumber'
          ) {
            obj[col.name] = coerceIfNumeric(val); // --- fix: coerce numeric strings to number
          }
        });
      };

      formData?.sections?.forEach((section) => {
        section.fields
          ?.filter((f) => f.type === FormConstants.Field_Types.TABLE_INPUT)
          ?.forEach((tbl) => {
            defaultValueSource[tbl.name]?.forEach?.((row: any) =>
              parseNestedTables(row, tbl.columns ?? [])
            );
          });
      });

      // --- fix: also coerce top‑level numeric fields (non‑table)
      formData?.sections?.forEach((section) => {
        section?.fields?.forEach((field) => {
          if (
            (field.validation_schema === 'number' || field.validation_schema === 'floatNumber') &&
            field.name in defaultValueSource
          ) {
            defaultValueSource[field.name] = coerceIfNumeric(defaultValueSource[field.name]);
          }
        });
      });

      const dv = formData?.sections?.reduce<Record<string, any>>((acc, section) => {
        section?.fields?.forEach((field) => {
          // For required boolean/radio_group fields without defaultValue, use undefined instead of ''
          // This prevents zod.coerce.boolean() from converting '' to false, which bypasses required validation
          if (
            field.required &&
            field.validation_schema === 'boolean' &&
            !defaultValueSource[field.name] &&
            field.defaultValue === undefined
          ) {
            acc[field.name] = undefined;
          } else {
            acc[field.name] = defaultValueSource[field.name] ?? field.defaultValue ?? '';
          }
        });
        return acc;
      }, {});

      const currentTranslate = i18next.getFixedT(i18next.language, 'form');
      const zodSchema = createFormSchema(formData, currentTranslate);
      setDefaultValues(dv);
      // console.log(dv, 'dv');
      setSchema(zodSchema);
    } catch (err) {
      console.error('Error fetching form data', err);
      const errMessage = 'Something went wrong while loading the form.';
      setError(errMessage);
      toast.error(err.message);
    }
  }, [
    fetchDefaultValues,
    stableFormDataParsed,
    isAdmin,
    module_name,
    personID,
    stableQueryDependencyIds,
    person_id,
  ]);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData]);

  if (error) {
    return (
      <Stack
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          my: 5,
          px: 2,
          py: 3,
          border: '1px solid #f44336',
          borderRadius: 2,
          color: '#f44336',
          backgroundColor: '#ffebee',
          fontWeight: 500,
          fontSize: '16px',
          textAlign: 'center',
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        {error}
      </Stack>
    );
  }

  if (!defaultValues || !schema || !formDataParsed) {
    return (
      <Stack sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 5 }}>
        <PulseLoader size={12} />
      </Stack>
    );
  }

  const evaluateConditions = (conditions: any, data: Record<string, any>): boolean => {
    if (!conditions) return true;

    const operator = conditions.logical_operator || 'AND';
    const rules = conditions.rules || [];

    const results = rules.map((rule: any) => {
      if (rule.conditions) {
        // nested condition group
        return evaluateConditions(rule.conditions, data);
        // eslint-disable-next-line no-else-return
      } else {
        const fieldValue = data[rule.field];
        switch (rule.operator) {
          case '==':
            return fieldValue === rule.value;
          case '!=':
            return fieldValue !== rule.value;
          case 'in':
            return Array.isArray(rule.value) && rule.value.includes(fieldValue);
          case 'not_in':
            return Array.isArray(rule.value) && !rule.value.includes(fieldValue);
          case '>':
            return fieldValue > rule.value;
          case '<':
            return fieldValue < rule.value;
          default:
            return false;
        }
      }
    });

    return operator === 'AND' ? results.every(Boolean) : results.some(Boolean);
  };

  const fetchProperties = async (selectedAction: any) => {
    try {
      const req_module_name = module_name || 'applications';
      if (!defaultData.RecordId) {
        toast.error('RecordId is missing');
      }

      const data: any = {
        sql_data: selectedAction?.properties_list_sql,
        record_id: defaultData?.RecordId || 0,
        is_hidden: false,
      };
      const encrypted = EncryptUtils.encryptedData(data);
      const resp = await getDndDraggableList(req_module_name, { tk: encrypted });

      const enriched = (resp.data || []).map((item: any, index: number) => ({
        ...item,
        Priority: index + 1,
      }));
      setProperties(enriched);
      // setPropertyDialogOpen(true);
    } catch (errMessage) {
      console.error('Error fetching property list:', errMessage);
      toast.error('Failed to fetch properties.');
    }
  };

  const handleStageChangeAction = async () => {
    // console.log(pendingAction, module_name, sub_module, defaultData, '001');
    setDialogOnSubmitLoading(true);
    if (
      !pendingAction ||
      !module_name ||
      !sub_module ||
      (!defaultData?.CaseId && !defaultData?.ComplianceReviewId)
    ) {
      setDialogOnSubmitLoading(false);
      return;
    }
    // console.log('inside handleStageChangeAction');
    if (pendingAction?.reason_required && !reason?.trim()) {
      setReasonError('Reason is required.');
      setDialogOnSubmitLoading(false);
      return;
    }

    // const template = pendingAction?.email_template;

    // const subject = await injectTemplateFields(
    //   template?.subject,
    //   template?.fields,
    //   queryDependencyIds
    // );
    // const body = await injectTemplateFields(template?.body, template?.fields, queryDependencyIds);
    const payload: any = {
      sql_data: formDataParsed?.form_actions?.sql_data,
      notification_sql_data: formDataParsed?.notification_sql_data,
      is_notification_enabled: formDataParsed?.is_notification_enabled,
      case_id: defaultData?.CaseId,
      user_id: defaultData?.UserId,
      ComplianceReviewId: defaultData?.ComplianceReviewId,
      PersonId: defaultData?.PersonId || defaultData?.PersonID,
      action: selectedActionValue,
      stage_reason: reason ?? null,
      generate_pdf: pendingAction?.generate_pdf || false,
      is_admin: isAdmin,
      action_type: selectedActionValue,
      admin_person_id: decodedToken?.payload?.PersonId,
      // emailTemplate: {
      //   email: queryDependencyIds?.Email,
      //   username: queryDependencyIds?.['ApplicantName'],
      //   subject,
      //   body,
      // },
    };

    // console.log(payload, 'payload from actions');

    if (pendingAction?.select_property && selectedPropertyId) {
      payload.SelectedPropertyID = selectedPropertyId;
    }

    try {
      await executeAdminActions(module_name, sub_module, {
        tk: EncryptUtils.encryptedData(payload),
      });

      toast.success(`Stage status successfully changed to "${toStage}".`);
      setActionDialogOpen(false);
      setSelectedActionValue('');
      setReason(null);
      setReasonError('');
      setSelectedPropertyId(null);
      await fetchFormData();
      setDialogOnSubmitLoading(false);
    } catch (err: any) {
      setDialogOnSubmitLoading(false);
      toast.error(err.message || 'Action failed');
    }
  };

  return (
    <>
      {formDataParsed?.form_actions?.options && defaultData !== null && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%', mb: 2 }}
        >
          {defaultData?.Stage && (
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary' }}>
                Stage:
              </Box>
              <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
                {defaultData?.Stage}
              </Box>
            </Typography>
          )}
          {hasPermission(formDataParsed?.form_actions) && (
            <Select
              size="small"
              sx={{
                maxWidth: 500,
                width: 250,
                mr: 2,
                borderColor: '#d1d5db',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d1d5db',
                },
              }}
              displayEmpty
              value={selectedActionValue}
              onChange={async (e) => {
                const value = e.target.value;
                setSelectedActionValue(value);

                const selected = formDataParsed?.form_actions?.options?.find(
                  (action: any) => action.value === value
                );

                if (!selected) return;

                setPendingAction(selected);
                setFromStage(defaultData?.Stage || '');
                setToStage(selected.label);
                // setActionDialogOpen(true);

                if (selected?.select_property) {
                  await fetchProperties(selected);
                  setPropertyDialogOpen(true);
                } else {
                  setActionDialogOpen(true);
                }
              }}
            >
              <MenuItem value="">Select an Action</MenuItem>
              {formDataParsed?.form_actions.options
                .filter((action: any) => evaluateConditions(action.conditions, defaultData))
                .map((action: any) => (
                  <MenuItem key={action.value} value={action.value}>
                    {action.label}
                  </MenuItem>
                ))}
            </Select>
          )}
        </Stack>
      )}
      {defaultValues && (
        <FormBuilder
          defaultValues={defaultValues}
          schema={schema}
          formDataParsed={formDataParsed}
          stageMessage={StageMessage}
          isFullApplicationEnabled={isFullApplicationEnabled}
          fullApplicationIncomeCategory={fullApplicationIncomeCategory}
          module_name={module_name}
          sub_module={sub_module}
          defaultData={defaultData}
          isAdmin={isAdmin}
          refreshForm={fetchFormData}
          onRefreshFullApplicationStatus={onRefreshFullApplicationStatus}
          scrollNeeded={scrollNeeded}
          signupType={signupType}
        />
      )}

      <Dialog
        open={actionDialogOpen}
        onClose={() => {
          setActionDialogOpen(false);
          setSelectedActionValue('');
          setSelectedPropertyId(null);
          setPendingAction(null);
        }}
      >
        <DialogTitle>Confirm Stage Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to change the stage status from <strong>{fromStage}</strong> to{' '}
            <strong>{toStage}</strong>?
          </Typography>
          {pendingAction?.reason_required && (
            <TextField
              label="Reason"
              fullWidth
              multiline
              value={reason}
              sx={{
                mt: 2,
              }}
              onChange={(e) => {
                setReason(e.target.value);
                setReasonError('');
              }}
              error={!!reasonError}
              helperText={reasonError}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionDialogOpen(false);
              setSelectedActionValue('');
              setReason(null);
              setReasonError('');
              setSelectedPropertyId(null);
              setPendingAction(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleStageChangeAction}
            loading={dialogOnSubmitLoading}
            color="primary"
            variant="contained"
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={propertyDialogOpen}
        onClose={() => setPropertyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Property</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Location</th>
                  <th>Sale Price</th>
                  <th>Select</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr key={property.PropertyId}>
                    <td>{property.Priority}</td>
                    <td>{property.Location}</td>
                    <td>{property.SalePrice}</td>
                    <td>
                      <Button
                        variant={
                          selectedPropertyId === property.PropertyId ? 'contained' : 'outlined'
                        }
                        onClick={() => setSelectedPropertyId(property.PropertyId)}
                      >
                        {selectedPropertyId === property.PropertyId ? 'Selected' : 'Select'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPropertyDialogOpen(false);
              setSelectedActionValue('');
              setSelectedPropertyId(null);
              setPendingAction(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setPropertyDialogOpen(false);
              setActionDialogOpen(true);
            }}
            variant="contained"
            disabled={!selectedPropertyId}
          >
            Confirm Selection
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
